import React, { useMemo } from "react";

/**
 * Presentation pieces shared by the mini-game admin pages: the stat cards, the
 * daily trend charts, the fortune wheel, and a percentage slider.
 *
 * All drawn as inline SVG rather than pulled from a charting library, so the
 * bundle gains nothing and the wheel is guaranteed to match the slice values
 * actually configured — it is rendered from the same array that is saved.
 */

/** One headline figure. */
export const StatCard = ({ label, value, suffix, tone = "text-gray-800", icon }) => (
  <div className="bg-white border border-gray-100 rounded-2xl px-5 py-4 shadow-sm">
    <div className="flex items-start justify-between">
      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">{label}</p>
      {icon && <span className="text-base leading-none">{icon}</span>}
    </div>
    <p className={`text-2xl font-bold mt-2 ${tone}`}>
      {typeof value === "number" ? value.toLocaleString() : value}
      {suffix && <span className="text-xs font-semibold text-gray-400 ml-1">{suffix}</span>}
    </p>
  </div>
);

/** Day label as "08 Sep", from a YYYY-MM-DD string. */
const shortDay = (iso) => {
  const [y, m, d] = String(iso).split("-").map(Number);
  const at = new Date(y, (m || 1) - 1, d || 1);
  return `${String(d).padStart(2, "0")} ${at.toLocaleString("en-US", { month: "short" })}`;
};

/**
 * Bar chart of daily sessions.
 *
 * Labels are thinned to roughly eight across the axis, so a 30-day window does
 * not collapse into unreadable overlapping text.
 */
export const DailyBars = ({ data = [], color = "#f59e0b" }) => {
  const max = Math.max(1, ...data.map((d) => d.sessions));
  const step = Math.ceil(data.length / 8);

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${Math.max(320, data.length * 44)} 210`} className="w-full h-[210px]">
        {[0, 0.25, 0.5, 0.75, 1].map((f) => (
          <g key={f}>
            <line x1="34" x2="100%" y1={20 + f * 150} y2={20 + f * 150} stroke="#f1f5f9" strokeWidth="1" />
            <text x="26" y={24 + f * 150} textAnchor="end" fontSize="9" fill="#94a3b8">
              {Math.round(max * (1 - f))}
            </text>
          </g>
        ))}
        {data.map((d, i) => {
          const h = (d.sessions / max) * 150;
          return (
            <g key={d.date}>
              <rect
                x={44 + i * 44}
                y={170 - h}
                width="26"
                height={Math.max(h, d.sessions > 0 ? 2 : 0)}
                rx="4"
                fill={color}
                fillOpacity="0.18"
                stroke={color}
                strokeWidth="1.5"
              />
              {i % step === 0 && (
                <text x={57 + i * 44} y="190" textAnchor="middle" fontSize="9" fill="#94a3b8">
                  {shortDay(d.date)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

/** Area chart of daily points, with a dot on each day. */
export const DailyArea = ({ data = [], color = "#c2410c" }) => {
  const max = Math.max(1, ...data.map((d) => d.points));
  const step = Math.ceil(data.length / 8);
  const width = Math.max(320, data.length * 44);

  const pointAt = (d, i) => {
    const x = data.length === 1 ? width / 2 : 44 + (i * (width - 70)) / (data.length - 1);
    return [x, 170 - (d.points / max) * 150];
  };

  const path = data.map((d, i) => pointAt(d, i).join(",")).join(" L ");

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${width} 210`} className="w-full h-[210px]">
        {[0, 0.25, 0.5, 0.75, 1].map((f) => (
          <g key={f}>
            <line x1="34" x2="100%" y1={20 + f * 150} y2={20 + f * 150} stroke="#f1f5f9" strokeWidth="1" />
            <text x="26" y={24 + f * 150} textAnchor="end" fontSize="9" fill="#94a3b8">
              {Math.round(max * (1 - f)).toLocaleString()}
            </text>
          </g>
        ))}
        {data.length > 0 && (
          <>
            <path d={`M ${path} L ${pointAt(data[data.length - 1], data.length - 1)[0]},170 L 44,170 Z`}
              fill={color} fillOpacity="0.12" />
            <path d={`M ${path}`} fill="none" stroke={color} strokeWidth="2" />
            {data.map((d, i) => {
              const [x, y] = pointAt(d, i);
              return <circle key={d.date} cx={x} cy={y} r="3" fill={color} />;
            })}
          </>
        )}
        {data.map((d, i) =>
          i % step === 0 ? (
            <text key={d.date} x={pointAt(d, i)[0]} y="190" textAnchor="middle" fontSize="9" fill="#94a3b8">
              {shortDay(d.date)}
            </text>
          ) : null
        )}
      </svg>
    </div>
  );
};

const WHEEL_COLORS = [
  "#ef4444", "#3b82f6", "#64748b", "#10b981", "#8b5cf6",
  "#f59e0b", "#ec4899", "#06b6d4", "#475569", "#eab308",
];

/**
 * The fortune wheel, drawn from the slice values themselves.
 *
 * Rendering from the same array that gets saved means the picture can never
 * disagree with the configuration — edit a slice and the wheel redraws.
 * `highlight` spins the pointer to a slice, used by the test simulator.
 */
export const FortuneWheel = ({ slices = [], highlight = null, size = 300 }) => {
  const n = Math.max(slices.length, 1);
  const r = size / 2;
  const slice = 360 / n;

  const wedges = useMemo(
    () =>
      slices.map((value, i) => {
        const from = (i * slice - 90) * (Math.PI / 180);
        const to = ((i + 1) * slice - 90) * (Math.PI / 180);
        const x1 = r + r * 0.92 * Math.cos(from);
        const y1 = r + r * 0.92 * Math.sin(from);
        const x2 = r + r * 0.92 * Math.cos(to);
        const y2 = r + r * 0.92 * Math.sin(to);
        const mid = (i * slice + slice / 2 - 90) * (Math.PI / 180);
        return {
          value,
          path: `M ${r} ${r} L ${x1} ${y1} A ${r * 0.92} ${r * 0.92} 0 ${slice > 180 ? 1 : 0} 1 ${x2} ${y2} Z`,
          tx: r + r * 0.62 * Math.cos(mid),
          ty: r + r * 0.62 * Math.sin(mid),
          rotate: i * slice + slice / 2,
        };
      }),
    [slices, slice, r]
  );

  return (
    <div className="relative" style={{ width: size, height: size + 14 }}>
      {/* Pointer, above the wheel, aimed at the highlighted slice. */}
      <svg
        width="26"
        height="18"
        viewBox="0 0 26 18"
        className="absolute left-1/2 -translate-x-1/2 z-10"
        style={{ top: 0 }}
      >
        <path d="M13 18 L0 0 L26 0 Z" fill="#f59e0b" />
      </svg>

      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{
          marginTop: 12,
          transition: "transform 3s cubic-bezier(.17,.67,.21,1)",
          transform: highlight == null ? "rotate(0deg)" : `rotate(${1440 - (highlight * slice + slice / 2)}deg)`,
        }}
      >
        <circle cx={r} cy={r} r={r - 2} fill="#fbbf24" />
        {wedges.map((w, i) => (
          <g key={i}>
            <path d={w.path} fill={WHEEL_COLORS[i % WHEEL_COLORS.length]} stroke="#0f172a" strokeWidth="2" />
            <text
              x={w.tx}
              y={w.ty}
              fill="#ffffff"
              fontSize={size / 20}
              fontWeight="700"
              textAnchor="middle"
              dominantBaseline="middle"
              transform={`rotate(${w.rotate} ${w.tx} ${w.ty})`}
            >
              {w.value}
            </text>
          </g>
        ))}
        <circle cx={r} cy={r} r={r * 0.11} fill="#0f172a" />
        <circle cx={r} cy={r} r={r * 0.08} fill="#fde047" />
      </svg>
    </div>
  );
};

/** A labelled percentage slider with its own colour and caption. */
export const PercentSlider = ({ label, value, onChange, hint, color = "#3b82f6", accentBg = "bg-white" }) => (
  <div className={`rounded-2xl border border-gray-100 px-4 py-3 ${accentBg}`}>
    <div className="flex items-center justify-between gap-3">
      <p className="text-sm font-semibold" style={{ color }}>
        {label}
      </p>
      <span className="text-[11px] font-bold px-2 py-0.5 rounded-md border border-gray-200 text-gray-600 bg-white">
        {Number(value) || 0}%
      </span>
    </div>
    <input
      type="range"
      min={0}
      max={100}
      value={Number(value) || 0}
      onChange={(e) => onChange(e.target.value)}
      className="w-full mt-2 accent-current"
      style={{ accentColor: color }}
    />
    {hint && <p className="text-[11px] mt-1" style={{ color }}>{hint}</p>}
  </div>
);
